import { capitalizeOnlyFirstLetter } from '@/util-conversion/misc';
import { forwardRef } from 'react';
import { fullstoryEvent } from '@/fullstory/utils/fullstory-event';
import { Link, LinkProps } from 'react-router';
import { Vehicle } from '../types';
import VehicleTile, { VehicleTileProps } from './vehicle-tile';
import { useDisableSpaNavigation } from '@/util-react-router/hooks/use-disable-spa-navigation';
import { useDisableLinkPrefetch } from '@/util-react-router/hooks/use-disable-link-prefetch';

interface VehicleTileLinkProps
  extends Omit<LinkProps, 'to'>,
    Pick<
      VehicleTileProps,
      'fetchSavingsCalculator' | 'savingsV2PillId' | 'showCoachMark' | 'fadeInImage'
    > {
  market?: string;
  vehicle: Vehicle;
  PUBLIC_CDN_URL: string;
  /** Used to track the sort rank of the vehicle tile when clicked */
  inventorySortRank: number;
  /** Used to track the selected sort option of the vehicle tile when clicked */
  inventorySortOption: string;
  isSavingsV2DataPresent?: boolean;
  /** Indicates if image loading into DOM should take priority */
  imageLoadPriority?: 'high' | 'low';
}

/**
 * Functional VehicleTile that links to the vehicle's detail page and tracks the click event.
 */
const VehicleTileLink = forwardRef<HTMLAnchorElement, VehicleTileLinkProps>(
  (
    {
      market,
      vehicle,
      PUBLIC_CDN_URL,
      inventorySortRank,
      inventorySortOption,
      fetchSavingsCalculator,
      showCoachMark,
      savingsV2PillId,
      onClick,
      isSavingsV2DataPresent,
      imageLoadPriority,
      fadeInImage,
      ...props
    }: VehicleTileLinkProps,
    ref
  ) => {
    const trackSortRankFullStory = () => {
      fullstoryEvent('vehicle_card_click', {
        inventory_id: vehicle.id,
        inventory_sort_rank: inventorySortRank,
        inventory_sort_option: inventorySortOption,
      });
      return true;
    };

    const image = vehicle.thumbnail_image?.path
      ? `${PUBLIC_CDN_URL}/${vehicle.thumbnail_image.path}.webp`
      : undefined;

    const { disableSpaNavigation } = useDisableSpaNavigation();
    const { disableLinkPrefetch } = useDisableLinkPrefetch();

    return (
      <Link
        ref={ref}
        to={`/inventory/${market}/${vehicle.id}`}
        reloadDocument={disableSpaNavigation}
        prefetch={disableLinkPrefetch ? 'none' : 'intent'}
        key={vehicle.id}
        onClick={(e) => {
          if (onClick) onClick(e);
          trackSortRankFullStory();
        }}
        {...props}
      >
        <VehicleTile
          image={image}
          modelYear={vehicle.model_year}
          make={vehicle.make}
          model={vehicle.model}
          trim={vehicle.trim}
          color={capitalizeOnlyFirstLetter(vehicle.exterior_color_basic)}
          price={vehicle.selected_plan.total_price_per_month}
          odometer={vehicle.odometer}
          earliestAvailableDate={vehicle.earliest_available_date}
          isRecentlyAdded={vehicle.recently_added}
          isOrderable={vehicle.is_orderable}
          isNewVehicle={vehicle.is_new_vehicle}
          pickupDays={vehicle.pickup_days}
          isElectric={vehicle.is_electric}
          totalSavingsPerYear={vehicle.savings?.total_savings}
          crossMarketRoute={vehicle.cross_market_route}
          shippingFeeBreakdown={vehicle.shipping_fee_breakdown}
          savingsCalculatorAvailable={
            vehicle.vehicle_savings_calculator_available && isSavingsV2DataPresent
              ? true
              : false
          }
          fetchSavingsCalculator={fetchSavingsCalculator}
          showCoachMark={showCoachMark}
          savingsV2PillId={savingsV2PillId}
          imageLoadPriority={imageLoadPriority}
          fadeInImage={fadeInImage}
        />
      </Link>
    );
  }
);

VehicleTileLink.displayName = 'VehicleTileLink';

export default VehicleTileLink;
